/**
 * hero.js — FIG. 01 (consultancy #coasters). Scrolling through the section assembles the coaster; the stage is
 * sticky in CSS (no JS pinning). Also: the home headline intro, and live recolouring from the colour picker.
 * Poster mode works everywhere (the poster shows the END state: coaster + phone with the menu); three.js
 * (../hero3d.js) is lazy-loaded on the first interaction, usually long before this section is in view.
 * Callout positions: poster → data-poster % (from blender/anchors_screen.py); live → projected 3D anchors.
 */
import { $, $$, clamp, reduce, navH, gsap, ScrollTrigger, RT } from "./core.js";

export function initHero() {
  // home headline: lines rise out of their masks, index rows rule in
  if (!reduce && $(".hero h1")) {
    gsap.from(".hero h1 .l > span", { yPercent: 105, duration: 1.1, ease: "expo.out", stagger: 0.08, delay: 0.1 });
    gsap.from(".index li", { opacity: 0, x: -12, duration: 0.6, ease: "power2.out", stagger: 0.06, delay: 0.5 });
  }
  const stage = $("#stage");
  if (!stage) return { live: () => false };
  const section = stage.closest("section");
  let colors = null;                                   // last pick from the colour picker, applied once 3D is live
  document.addEventListener("coaster:colors", (e) => { colors = e.detail; hero3d?.setColors(colors.base, colors.accent); });
  let engraving = "rhino";                             // plate engraving: "rhino" | "logo" (picker under the colours)
  document.addEventListener("coaster:engraving", (e) => { engraving = e.detail; hero3d?.setEngraving(engraving); });
  const canvas = $("#stageCanvas"), pct = $("#asmPct"), bar = $(".readout .bar"), ping = $("#ping");
  const callouts = $$(".co", stage).map((el) => ({
    el, anchor: el.dataset.anchor, poster: el.dataset.poster.split(",").map(Number),
    dot: $(".co__dot", el), line: $(".co__line", el), elbow: $(".co__elbow", el), lbl: $(".co__lbl", el),
  }));
  let hero3d = null, progress = 0, points = null;

  const webgl2 = () => { try { return !!document.createElement("canvas").getContext("webgl2"); } catch { return false; } };
  // ?no3d forces the static poster (QA: shows what no-WebGL / reduced-motion / save-data visitors get)
  const can3D = !reduce && webgl2() && !new URLSearchParams(location.search).has("no3d")
    && !(navigator.connection && navigator.connection.saveData) && (navigator.deviceMemory || 8) >= 3;

  /** the poster is a centred 4:5 box: height-fit on wide stages, width-fit on narrow ones (same as the 3D camera) */
  const pingPoster = { nfc: ping.dataset.posterNfc.split(",").map(Number), scan: ping.dataset.posterScan.split(",").map(Number) };
  function posterPoints() {
    const W = stage.clientWidth, H = stage.clientHeight;
    const bw = Math.min(W, H * 0.8), bh = bw * 1.25, left = (W - bw) / 2, top = (H - bh) / 2;
    const at = ([px, py]) => ({ x: left + (px / 100) * bw, y: top + (py / 100) * bh });
    const p = { nfc: at(pingPoster.nfc), scan: at(pingPoster.scan) };
    for (const c of callouts) p[c.anchor] = at(c.poster);
    return p;
  }

  /** dot → leader → elbow → label. Two label columns: A+B on the right, C+D on the left (data-side), each pushed
   *  apart vertically so labels never overlap; a callout whose part is out of frame is hidden. */
  function layout(p) {
    if (!p) return;
    const W = stage.clientWidth, H = stage.clientHeight, small = W < 600, edge = small ? 12 : 28;
    const lblH = Math.max(...callouts.map((c) => c.lbl.offsetHeight));
    const gap = lblH + 10, top = 64 + lblH / 2, bottom = H - (small ? 70 : 56) - lblH / 2;
    for (const side of ["r", "l"]) {
      const col = callouts.filter((c) => c.el.dataset.side === side);
      if (!col.length) continue;
      const lblW = Math.max(...col.map((c) => c.lbl.offsetWidth));
      const colX = side === "r" ? W - edge - lblW : edge;                 // label column
      const ex = side === "r" ? colX - 18 : colX + lblW + 18;              // where the leader turns
      const items = col.map((c) => ({ c, x: p[c.anchor].x, y: p[c.anchor].y, ly: p[c.anchor].y })).sort((a, b) => a.y - b.y);
      items.forEach((it, i) => { it.ly = clamp(Math.max(it.ly, i ? items[i - 1].ly + gap : top), top, bottom); });
      for (let i = items.length - 2; i >= 0; i--) items[i].ly = Math.min(items[i].ly, items[i + 1].ly - gap);
      for (const { c, x, y, ly } of items) {
        c.el.classList.toggle("is-off", x < 0 || x > W || y < 0 || y > H);   // part not in frame yet (the phone, early on)
        const x0 = Math.min(x, ex), len = Math.abs(ex - x);
        c.dot.style.transform = `translate(${x}px,${y}px)`;
        c.line.style.transform = `translate(${x0}px,${y}px)`;
        c.line.style.width = `${len}px`;
        c.elbow.style.transform = `translate(${ex}px,${Math.min(y, ly)}px)`;
        c.elbow.style.height = `${Math.abs(ly - y) + 2}px`;
        c.lbl.style.transform = `translate(${colX}px,${ly - lblH / 2}px)`;
      }
    }
    // NFC beam: dashed signal from the tag (under the plate) up to the phone's touch point, waves at the tag
    if (p.nfc && p.scan) {
      const dx = p.scan.x - p.nfc.x, dy = p.scan.y - p.nfc.y;
      ping.style.transform = `translate(${p.nfc.x}px,${p.nfc.y}px)`;
      ping.style.setProperty("--len", `${Math.hypot(dx, dy)}px`);
      ping.style.setProperty("--ang", `${Math.atan2(dy, dx)}rad`);
      // label at the phone end of the beam, just above it and to the left: clear of the plate and of callouts C/D
      ping.style.setProperty("--mx", `${dx}px`); ping.style.setProperty("--my", `${dy - 26}px`);
    }
  }

  function setProgress(p) {
    progress = p;
    const t = clamp(p / 0.5, 0, 1);             // 100% once the phone hovers over the plate (hero3d.js P_PHONE)
    pct.textContent = String(Math.round(t * 100)).padStart(3, "0") + "%";
    bar.style.setProperty("--p", t.toFixed(3));
    ping.classList.toggle("on", p > 0.52);      // NFC ping as the screen lights up, stays on while the menu holds
    hero3d?.setProgress(p);
  }

  points = posterPoints();
  layout(points);
  callouts.forEach((c, i) => setTimeout(() => c.el.classList.add("on"), reduce ? 0 : 500 + i * 140));
  new ResizeObserver(() => { if (!hero3d) points = posterPoints(); layout(points); }).observe(stage);

  if (!reduce) {
    const mm = gsap.matchMedia();
    mm.add("(min-width: 961px)", () => {           // the stage is sticky; progress = scroll through the whole section
      ScrollTrigger.create({ trigger: section, start: () => `top ${navH()}px`, end: "bottom bottom", scrub: true, onUpdate: (st) => setProgress(st.progress) });
    });
    mm.add("(max-width: 960px)", () => {           // phones: the stage pins under the nav inside its tall track (CSS) —
      // progress runs from the moment it pins until the track ends, so every step of the scan is seen in full
      ScrollTrigger.create({ trigger: stage.parentElement, start: () => `top ${navH()}px`, end: "bottom bottom", scrub: true,
        invalidateOnRefresh: true, onUpdate: (st) => setProgress(st.progress) });
    });
  }

  if (can3D) {
    const start = async () => {
      try {
        const { createHero } = await import("../hero3d.js");
        hero3d = await createHero({ canvas, url: `/assets/3d/coaster.glb?v=${RT.v || ""}`, onFrame: (p) => { points = p; layout(p); } });
        hero3d.setProgress(progress);
        if (colors) hero3d.setColors(colors.base, colors.accent);
        hero3d.setEngraving(engraving);
        stage.classList.add("is-live");
        document.documentElement.classList.add("has-3d");   // shows the engraving picker (it needs the live model)
        stage.addEventListener("pointermove", (e) => {
          const r = stage.getBoundingClientRect();
          hero3d?.setPointer(((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1);
        });
        stage.addEventListener("pointerleave", () => hero3d?.setPointer(0, 0));
        stage.addEventListener("hero:lost", () => { stage.classList.remove("is-live"); document.documentElement.classList.remove("has-3d"); hero3d = null; points = posterPoints(); layout(points); });
      } catch (err) {
        console.warn("[rhino] 3D unavailable, keeping the poster", err);
      }
    };
    // Load three.js on the first sign of a real visitor (scroll, touch, mouse, key) — keeps the initial load light
    // (Lighthouse/TBT). The poster shows the final frame; by the time the visitor reaches the section the 3D is live.
    const EVENTS = ["wheel", "touchstart", "pointermove", "keydown", "scroll"];
    let started = false;
    const kick = () => {
      if (started) return; started = true;
      EVENTS.forEach((ev) => removeEventListener(ev, kick));
      (window.requestIdleCallback || ((f) => setTimeout(f, 1)))(start, { timeout: 300 });
    };
    EVENTS.forEach((ev) => addEventListener(ev, kick, { passive: true, once: true }));
  }
  return { live: () => !!hero3d };
}
